"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocalStorage = void 0;
const lodash_es_1 = require("lodash-es");
const module_1 = require("../../_internal/storage/module");
let _GLOBAL_PREFIX = '';
function convertToStorage(value, nullable) {
    if (nullable) {
        // JSON.stringify converts `undefined` to `null` so we store
        // these values as strings and convert them back later
        if (typeof value === 'undefined') {
            return 'undefined';
        }
        if (value === null) {
            return 'null';
        }
    }
    return value;
}
function convertFromStorage(value, nullable) {
    if (nullable) {
        // JSON.stringify converts `undefined` to `null` so we stored
        // these values as strings and convert them back here
        if (value === 'undefined') {
            return undefined;
        }
        if (value === 'null') {
            return null;
        }
    }
    return value;
}
class LocalStorage extends module_1.Storage {
    constructor(fields, config) {
        var _a;
        super(fields);
        this._prefix = '';
        this.StorageProvider = new (class {
            constructor(parent) {
                this.getRaw = (0, lodash_es_1.memoize)((name) => {
                    const res = window.localStorage.getItem(this.parent._getIdentifier(name));
                    if (!res) {
                        return;
                    }
                    const [cachedAtTime, value, nullable] = JSON.parse(res);
                    return [cachedAtTime, value, nullable];
                });
                this.getMemoized = (0, lodash_es_1.memoize)((name) => {
                    const raw = this.getRaw(name);
                    if (!raw) {
                        return;
                    }
                    const [, value, nullable] = raw;
                    return convertFromStorage(value, nullable ? true : false);
                });
                this.set = (name, value) => {
                    const existingRawValue = this.getRaw(name);
                    const field = this.parent._fields[name];
                    const convertedValue = convertToStorage(value, field.nullable);
                    const valueArr = [
                        // Save current timestamp for caching mechanism
                        Math.floor(Date.now() / 1000),
                        convertedValue,
                    ];
                    if (field.nullable) {
                        // Set nullable flag
                        valueArr.push(1);
                    }
                    // Update item in local storage and clear the raw cache
                    window.localStorage.setItem(this.parent._getIdentifier(name), JSON.stringify(valueArr));
                    this.getRaw.cache.delete(name);
                    // Check if the existing raw value matches the new raw value.
                    // If it doesn't, only then should we clear the memo cache!
                    // If they do match, then we want it to continue returning the same reference.
                    if (existingRawValue && JSON.stringify(existingRawValue[1]) !== JSON.stringify(convertedValue)) {
                        this.getMemoized.cache.delete(name);
                    }
                };
                this.remove = (name) => {
                    window.localStorage.removeItem(this.parent._getIdentifier(name));
                    this.getRaw.cache.delete(name);
                    this.getMemoized.cache.delete(name);
                };
                this.has = (name) => {
                    const res = window.localStorage.getItem(this.parent._getIdentifier(name));
                    return res !== null;
                };
                this.parent = parent;
            }
            shouldRemove(name) {
                const raw = this.getRaw(name);
                if (!raw) {
                    return;
                }
                const [cachedAtTime] = raw;
                const { TTL } = this.parent._fields[name];
                const expiresAt = typeof TTL === 'number' && typeof cachedAtTime === 'number' ? TTL + cachedAtTime : undefined;
                const now = Math.floor(Date.now() / 1000);
                if (typeof expiresAt === 'number' && now >= expiresAt) {
                    return true;
                }
                return false;
            }
            get(name) {
                const raw = this.getRaw(name);
                if (!raw) {
                    return;
                }
                if (this.shouldRemove(name)) {
                    // Invalidate cache and return nothing
                    this.parent.remove(name);
                    return;
                }
                return this.getMemoized(name);
            }
        })(this);
        this._prefix = (_a = config === null || config === void 0 ? void 0 : config.prefix) !== null && _a !== void 0 ? _a : '';
    }
    _getIdentifier(name) {
        var _a;
        const field = this._fields[name];
        const key = String((_a = field.key) !== null && _a !== void 0 ? _a : name);
        return `${_GLOBAL_PREFIX}${this._prefix}${key}`;
    }
    /**
     * Set prefix for all keys using this LocalStorage provider
     */
    static setGlobalPrefix(prefix) {
        _GLOBAL_PREFIX = prefix;
    }
}
exports.LocalStorage = LocalStorage;
